/* ===================================================================
   Welcome -- the LINEAR onboarding CHUTE (2026-07-09, "Temple Grandin cattle
   chute"). EXACTLY four steps, one primary action per full-screen page,
   AUTO-ADVANCING on completion, with a slim "Step X of 4" cue in the footer.
   Each step is its OWN deep-linkable route; navigation is driven by nextStep()
   from DERIVED completion state (real account data), never a stored index.

     Step 1  Sign up            -> routes/Auth (before the chute). 0-friction.
     Step 2  /welcome/connect   -> Connect + CONFIRM Google Business Profile
                                   (keeps the wrong-listing guard; explicit
                                   affirmative required; skippable but skip !=
                                   done). CARD-FREE.
     Step 3  /welcome/test      -> Send yourself a test review request = the
                                   instant Aha. EMAIL to the owner's own inbox.
                                   CARD-FREE.
     Step 4  /welcome/done      -> Into the dashboard ("where the real fun
                                   begins") + kicks off the post-chute guided
                                   tour.

   The chute has NO card. The credit-card ask stays at the FIRST REAL customer
   send (InlineCardStep), post-chute. High-contrast token styling (tokens.css);
   dark-mode flips automatically because this renders outside the app shell.
   =================================================================== */
import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { BrandMark } from '../components/BrandMark';
import GoogleG from '../components/GoogleG';
import {
  useChuteProgress,
  useMe,
  useBusiness,
  useUpdateBusiness,
} from '../hooks';
import { useSession } from '../auth/useSession';
import {
  api,
  ApiError,
  startGoogleConnect,
  type TestSendResult,
  type GbpManagedLocation,
} from '../lib/api';
import { parseReviewLink } from '../lib/googleReviewLink';
import { apiErrorMessage } from '../lib/display';
import { track } from '../lib/analytics';
import { setChuteTestSent } from '../lib/chuteState';
import { CHUTE_RESUME_FLAG, consumeChuteGoogleReturn } from '../lib/chuteResume';
import '../styles/welcome.css';

const STEP_IDS = ['connect', 'test', 'done'] as const;
type ChuteRouteStep = (typeof STEP_IDS)[number];

/* ---- small inline glyphs ---- */
function Arrow() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} aria-hidden="true">
      <path d="M5 12h14M13 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
function BigCheck() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.6} aria-hidden="true">
      <path d="M5 12l5 5L20 6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export function Welcome() {
  const params = useParams();
  const navigate = useNavigate();
  const step = (params.step ?? '') as string;
  const progress = useChuteProgress();
  const { data: me } = useMe();
  const { user } = useSession();
  const clientId = me?.client_id;

  // ---- slim PERCENTAGE progress (endowed-progress: never 0%) ----
  // Sign up = 25% (behind us), connect = 50%, test = 75%, done animates to 100%.
  // The bar fills as they advance; a CSS width transition animates each jump
  // (including 75% -> 100% on the done step). Numeric "Step X of 4" wording is
  // gone -- the bar + percent replaces it.
  const targetPct = step === 'test' ? 75 : step === 'done' ? 100 : 50;
  const [barPct, setBarPct] = useState(50);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setBarPct(targetPct));
    return () => cancelAnimationFrame(raf);
  }, [targetPct]);

  // ---- index redirect: /welcome (no step) -> the derived nextStep ----
  useEffect(() => {
    if (step) return;
    if (progress.loading) return;
    navigate(progress.nextStep.route, { replace: true });
  }, [step, progress.loading, progress.nextStep.route, navigate]);

  // ---- guard: an unknown step routes back to nextStep ----
  useEffect(() => {
    if (!step) return;
    if (!(STEP_IDS as readonly string[]).includes(step) && !progress.loading) {
      navigate(progress.nextStep.route, { replace: true });
    }
  }, [step, progress.loading, progress.nextStep.route, navigate]);

  // ---- once the chute is complete, never trap the owner here ----
  useEffect(() => {
    if (progress.loading) return;
    if (progress.complete && step !== 'done') {
      navigate('/welcome/done', { replace: true });
    }
  }, [progress.complete, progress.loading, step, navigate]);

  if (!step || progress.loading) {
    return (
      <div className="wl">
        <div className="wl__panel wl__panel--load" aria-busy="true">
          <BrandMark size={30} />
          <div className="wl__loadbar" />
        </div>
      </div>
    );
  }

  const current = (STEP_IDS as readonly string[]).includes(step)
    ? (step as ChuteRouteStep)
    : 'connect';

  return (
    <div className="wl">
      <header className="wl__top">
        <BrandMark size={30} />
      </header>

      <main className="wl__panel">
        {current === 'connect' && <ConnectStep clientId={clientId} />}
        {current === 'test' && (
          <TestStep clientId={clientId} defaultEmail={user?.email ?? ''} />
        )}
        {current === 'done' && <DoneStep />}
      </main>

      {/* Slim percentage progress bar (endowed progress). Fills as they advance;
          replaces the old numeric step counter. The fill %, the fill's diagonal
          leading edge, and the black slant stroke are all driven off the single
          --fill custom property so they stay perfectly aligned. */}
      <footer className="wl__foot">
        <div
          className="wl__bar"
          role="progressbar"
          aria-valuenow={targetPct}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label="Setup progress"
          style={{ '--fill': `${barPct}%` } as CSSProperties}
        >
          <div className="wl__bar__fill" />
          {/* The 45deg black slant stroke that outlines the gold fill's diagonal
              leading edge (cuts against the candy-stripe). Decorative. */}
          <div className="wl__bar__edge" aria-hidden="true" />
        </div>
        <span className="wl__pct">{targetPct}%</span>
      </footer>
    </div>
  );
}

/* ===================================================================
   Step 2 -- Connect Google Business Profile (Steve's spec, 2026-07-15).

   Two screens, nothing more:
     SCREEN 1 (no OAuth token yet): a headline + ONE "Connect to Google" button.
       No subheadline, no search box, no card, no skip. The Places / "find my
       business" name-search is GONE from this flow entirely -- it returns
       ZERO_RESULTS for a service-area business and never worked for our ICP.
     After OAuth (a token exists but the listing isn't confirmed yet) we resolve
       the owner's GBP-managed locations with the EXISTING gbp-locations endpoint:
         * exactly one manageable location  -> AUTO-confirm it and advance (95%
           case; THIS is the fix for the connect-bounce -- OAuth used to save the
           token but never confirm a listing, so google_connection_status stayed
           below 'connected' and the chute/PostAuthLanding bounced the owner
           right back to the connect step "as if not connected").
         * multiple                         -> a MINIMAL plain pick list (no search).
         * none / GBP-unavailable / a confirm failure -> SCREEN 2.
     SCREEN 2 (fallback -- shows only when connect didn't resolve a listing):
       honest copy + "paste your Google review link" -> the EXISTING
       connect-review-link normalizer. No search, no manual business-details form.

   Reuses existing endpoints only (OAuth connect, gbp-locations,
   confirm-gbp-location, connect-review-link). On any successful connect the
   canonical google_connection_status flips to 'connected' and the auto-advance
   effect moves the owner to the test step.
   =================================================================== */
type ConnectPhase = 'idle' | 'resolving' | 'picking' | 'fallback';

function ConnectStep({ clientId }: { clientId: string | undefined }) {
  const navigate = useNavigate();
  const business = useBusiness();
  const { data: me } = useMe();
  const queryClient = useQueryClient();

  const googleStatus =
    business.data?.google_connection?.google_connection_status ??
    me?.google_connection_status ??
    'not_started';
  const googleConnected = googleStatus === 'connected';
  const tokenValid =
    business.data?.google_connection?.oauth_token_valid ??
    me?.oauth_token_valid ??
    false;

  // AUTO-ADVANCE the moment the canonical status is 'connected' (whether from an
  // auto-confirmed single location, a picked one, or a pasted review link).
  const advancedRef = useRef(false);
  useEffect(() => {
    if (googleConnected && !advancedRef.current) {
      advancedRef.current = true;
      track('chute_step_complete', { step: 'connect' });
      track('google_connected', {});
      navigate('/welcome/test');
    }
  }, [googleConnected, navigate]);

  // ---- Post-OAuth location resolution + the paste-link fallback ----
  const [phase, setPhase] = useState<ConnectPhase>('idle');
  const [locations, setLocations] = useState<GbpManagedLocation[] | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const resolveStartedRef = useRef(false);
  const [pasteUrl, setPasteUrl] = useState('');
  const [pasting, setPasting] = useState(false);
  const [pasteErr, setPasteErr] = useState<string | null>(null);
  const pasteParsed = parseReviewLink(pasteUrl);
  // Why the connect didn't resolve a listing. gbp-locations returns a `reason`
  // ('gbp_not_approved' | 'error' | 'not_connected'); we also read the OAuth
  // return status the callback bounced back (stashed across the chute resume).
  const [fallbackReason, setFallbackReason] = useState<string | null>(null);
  // The OAuth callback flags scope_insufficient when the owner completed sign-in
  // but did NOT grant "Manage your Google Business Profile". Consumed ONCE.
  const [googleReturn] = useState<string | null>(() => consumeChuteGoogleReturn());
  // A scope problem = the owner signed in but withheld Business Profile access.
  // Surface a clear reconnect prompt instead of silently dropping to paste-link.
  const scopeInsufficient =
    googleReturn === 'scope_insufficient' ||
    fallbackReason === 'gbp_not_approved' ||
    me?.google_health_status === 'scope_insufficient';

  // Confirm a managed location. On success the canonical status flips to
  // 'connected' (a managed listing carries its own review link) and the
  // auto-advance effect fires. A confirm failure -> the honest paste-link screen.
  const confirmLocation = useCallback(
    async (loc: GbpManagedLocation) => {
      setConfirmingId(loc.location_id);
      try {
        await api.confirmGbpLocation(loc.location_id);
        await queryClient.invalidateQueries();
      } catch {
        setConfirmingId(null);
        setPhase('fallback');
      }
    },
    [queryClient],
  );

  // Once a token exists but we're not yet connected, resolve the owner's managed
  // GBP locations ONCE and route: single -> auto-confirm, many -> pick, none -> fallback.
  useEffect(() => {
    if (!tokenValid || googleConnected || resolveStartedRef.current) return;
    resolveStartedRef.current = true;
    setPhase('resolving');
    const ctrl = new AbortController();
    api
      .listGbpLocations(ctrl.signal)
      .then((res) => {
        const locs = res.locations ?? [];
        setLocations(locs);
        if (res.reason || locs.length === 0) {
          setFallbackReason(res.reason ?? null);
          setPhase('fallback');
        } else if (locs.length === 1) {
          void confirmLocation(locs[0]);
        } else {
          setPhase('picking');
        }
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setPhase('fallback');
      });
    return () => ctrl.abort();
  }, [tokenValid, googleConnected, confirmLocation]);

  function connectGoogle() {
    if (!clientId) return;
    // Mark the chute so the post-OAuth return (which lands on /integrations)
    // bounces back into the chute (ChuteResumeSync in App).
    try {
      sessionStorage.setItem(CHUTE_RESUME_FLAG, '1');
    } catch {
      /* non-fatal */
    }
    void startGoogleConnect(clientId);
  }

  async function connectByPaste() {
    setPasteErr(null);
    const parsed = parseReviewLink(pasteUrl);
    if (!parsed.valid) {
      setPasteErr('Paste your Google review link (from your Google Business Profile).');
      return;
    }
    setPasting(true);
    try {
      await api.connectReviewLink(parsed.raw);
      await queryClient.invalidateQueries();
    } catch (e) {
      setPasteErr(
        apiErrorMessage(e, "We couldn't connect that link. Double-check it and try again."),
      );
    } finally {
      setPasting(false);
    }
  }

  // Connected -> the auto-advance effect is navigating away; render nothing.
  if (googleConnected) return null;

  // SCREEN 1 -- no token yet. Headline + ONE button. When the owner just
  // completed sign-in WITHOUT granting Business Profile access (scope_insufficient),
  // the callback stored nothing, so we land back here -- show a clear banner
  // telling them to reconnect and approve the permission, instead of silently
  // looping them on the same button with no explanation.
  if (!tokenValid) {
    return (
      <>
        <h1 className="wl__h">Connect your Google Business Profile</h1>
        {scopeInsufficient && (
          <div className="wl__err" role="alert">
            You signed in, but didn&apos;t grant Business Profile access. Reconnect
            and approve <strong>&ldquo;Manage your Google Business Profile&rdquo;</strong>{' '}
            so we can load your locations, import your reviews, and post replies.
          </div>
        )}
        <button
          className="btn btn--primary btn--block wl__gbtn"
          onClick={connectGoogle}
          disabled={!clientId}
        >
          <GoogleG size={20} /> {scopeInsufficient ? 'Reconnect to Google' : 'Connect to Google'}
        </button>
      </>
    );
  }

  // Token exists: resolving the location, or auto-confirming the single one.
  if (phase === 'resolving' || confirmingId) {
    return (
      <>
        <h1 className="wl__h">Finishing your connection</h1>
        <div className="wl__card wl__card--load" aria-busy="true">
          <div className="wl__loadbar" />
        </div>
      </>
    );
  }

  // Multiple managed locations -> a minimal plain pick list (NO search box).
  if (phase === 'picking' && locations && locations.length > 1) {
    return (
      <>
        <h1 className="wl__h">Which business is yours?</h1>
        <div className="wl__picklist">
          {locations.map((loc) => (
            <button
              key={loc.location_id}
              className="wl__pick"
              onClick={() => void confirmLocation(loc)}
              disabled={confirmingId !== null}
            >
              <span className="wl__pick__name">{loc.name}</span>
              {loc.address && <span className="wl__pick__addr">{loc.address}</span>}
            </button>
          ))}
        </div>
      </>
    );
  }

  // SCREEN 2 -- fallback. Connect didn't resolve a listing. When the cause is a
  // scope problem (the owner withheld Business Profile access, so gbp-locations
  // could not enumerate), LEAD with a reconnect prompt -- the real fix -- and
  // keep the paste-link as an explicit secondary option, never the silent default.
  if (scopeInsufficient) {
    return (
      <>
        <h1 className="wl__h">Grant Business Profile access</h1>
        <div className="wl__err" role="alert">
          You signed in with Google, but didn&apos;t grant permission to manage your
          Business Profile, so we can&apos;t load your locations. Reconnect and approve{' '}
          <strong>&ldquo;Manage your Google Business Profile&rdquo;</strong>.
        </div>
        <button
          className="btn btn--primary btn--block wl__gbtn"
          onClick={connectGoogle}
          disabled={!clientId}
        >
          <GoogleG size={20} /> Reconnect to Google
        </button>
        <p className="wl__sub" style={{ marginTop: 18 }}>
          Or, if you&apos;d rather not reconnect, paste your Google review link and
          we&apos;ll connect it that way.
        </p>
        <label className="wl__fld">
          <span className="wl__lab">Your Google review link</span>
          <input
            className="wl__inp"
            value={pasteUrl}
            placeholder="https://g.page/r/..."
            inputMode="url"
            autoComplete="off"
            onChange={(e) => {
              setPasteUrl(e.target.value);
              setPasteErr(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && pasteParsed.valid) void connectByPaste();
            }}
          />
        </label>
        {pasteErr && (
          <div className="wl__err" role="alert">
            {pasteErr}
          </div>
        )}
        <button
          className="btn btn--ghost btn--block"
          onClick={() => void connectByPaste()}
          disabled={pasting || !pasteParsed.valid}
        >
          {pasting ? 'Connecting...' : 'Connect this link instead'}
        </button>
      </>
    );
  }

  // SCREEN 2 (no scope problem) -- e.g. the account manages no listings or a
  // transient enumerate error: paste the review link.
  return (
    <>
      <h1 className="wl__h">Add your Google review link</h1>
      <p className="wl__sub">
        We couldn&apos;t automatically connect your Google Business Profile. Paste
        your Google review link and we&apos;ll connect it.
      </p>
      <label className="wl__fld">
        <span className="wl__lab">Your Google review link</span>
        <input
          className="wl__inp"
          value={pasteUrl}
          placeholder="https://g.page/r/..."
          inputMode="url"
          autoComplete="off"
          onChange={(e) => {
            setPasteUrl(e.target.value);
            setPasteErr(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && pasteParsed.valid) void connectByPaste();
          }}
        />
      </label>
      {pasteErr && (
        <div className="wl__err" role="alert">
          {pasteErr}
        </div>
      )}
      <button
        className="btn btn--primary btn--block"
        onClick={() => void connectByPaste()}
        disabled={pasting || !pasteParsed.valid}
      >
        {pasting ? 'Connecting...' : 'Connect this link'}
      </button>
    </>
  );
}

/* ===================================================================
   Step 3 -- Send yourself a test review request (the instant Aha).
   =================================================================== */
function TestStep({
  clientId,
  defaultEmail,
}: {
  clientId: string | undefined;
  defaultEmail: string;
}) {
  const navigate = useNavigate();
  // `null` = untouched -> show the signed-in owner's email (which may resolve a
  // tick after mount). Once the owner types (even to clear) it becomes a string
  // and we stop falling back. No effect + no set-state-in-effect.
  const [typed, setTyped] = useState<string | null>(null);
  const email = typed ?? defaultEmail;
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [result, setResult] = useState<TestSendResult | null>(null);

  async function send() {
    setErr(null);
    const to = email.trim();
    if (!to || !to.includes('@') || !to.split('@')[1]?.includes('.')) {
      setErr('Enter a valid email address so we can send you the test.');
      return;
    }
    setBusy(true);
    try {
      const res = await api.testSend({ to_email: to });
      if (res.sent) {
        setChuteTestSent(clientId, res.to ?? to);
        track('chute_step_complete', { step: 'test' });
        track('first_request_sent', {});
        setResult(res);
        // Auto-advance to the finish line after the owner sees the confirmation.
        window.setTimeout(() => navigate('/welcome/done'), 1400);
      } else {
        setErr("That didn't send. Give it another try.");
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 400) {
        setErr('Enter a valid email address so we can send you the test.');
      } else {
        setErr("That didn't send. Give it another try.");
      }
    } finally {
      setBusy(false);
    }
  }

  if (result?.sent) {
    return (
      <>
        <div className="wl__badge wl__badge--go" aria-hidden="true">
          <BigCheck />
        </div>
        <h1 className="wl__h">Check your inbox</h1>
        <p className="wl__sub">
          Your test review request is on its way to <strong>{result.to}</strong>. Open
          it to see exactly what your customers get.
        </p>
        {result.link_is_placeholder && (
          <div className="wl__note">
            It used an example review link for now. Connect your Google Business
            Profile and your real link goes in automatically.
          </div>
        )}
        <button className="btn btn--primary btn--block" onClick={() => navigate('/welcome/done')}>
          Continue <Arrow />
        </button>
      </>
    );
  }

  return (
    <>
      <h1 className="wl__h">Send yourself a test review</h1>
      <p className="wl__sub">
        Send one review request to your own inbox and see what your customers
        will experience.
      </p>

      <label className="wl__fld">
        <span className="wl__lab">Your email</span>
        <input
          className="wl__inp"
          type="email"
          autoComplete="email"
          inputMode="email"
          placeholder=""
          value={email}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') void send();
          }}
        />
      </label>

      {err && (
        <div className="wl__err" role="alert">
          <span>{err}</span>{' '}
          <a className="wl__help" href="mailto:support@rhodyreviews.com">
            Need help? Contact support.
          </a>
        </div>
      )}

      <button className="btn btn--primary btn--block" onClick={() => void send()} disabled={busy}>
        {busy ? 'Sending...' : 'Send me the test'} {!busy && <Arrow />}
      </button>
    </>
  );
}

/* ===================================================================
   Step 4 -- Into the dashboard + kick off the guided tour.
   =================================================================== */
function DoneStep() {
  const navigate = useNavigate();
  const business = useBusiness();
  const updateBusiness = useUpdateBusiness();
  const seededRef = useRef(false);

  // PRE-ASSUME brand voice (mission: default to the #1 most-commonly-picked
  // option so it is editable-not-blank; the post-chute tour points them to
  // tweak it). "Warm & personal" is the most common tone. Best-effort + only
  // when nothing is set -- never clobbers a real value.
  useEffect(() => {
    if (seededRef.current) return;
    if (business.isLoading) return;
    const bv = business.data?.brand_voice;
    const hasVoice = Boolean(
      bv &&
        (String(bv.tone ?? '').trim() ||
          String(bv.signature ?? '').trim() ||
          String(bv.sample_phrases ?? '').trim()),
    );
    if (hasVoice) {
      seededRef.current = true;
      return;
    }
    seededRef.current = true;
    void updateBusiness
      .save({
        brand_voice: {
          ...(bv ?? {}),
          tone: 'Warm & personal',
        },
      })
      .catch(() => {
        /* best-effort default; the owner can set voice in the tour/Settings */
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business.isLoading, business.data]);

  function goToDashboard() {
    track('chute_complete', {});
    navigate('/dashboard');
    // Kick off the post-chute guided tour once the shell mounts.
    window.setTimeout(() => {
      window.dispatchEvent(new CustomEvent('rhody:start-setup-tour'));
    }, 600);
  }

  return (
    <>
      <div className="wl__badge wl__badge--go" aria-hidden="true">
        <BigCheck />
      </div>
      <h1 className="wl__h">You&apos;re set up. Now the real fun begins.</h1>
      <p className="wl__sub">
        Your review engine is live. Next, a quick guided tour shows you where to
        tweak your brand voice, add your review widget, and bring in your
        customers so the 5-stars start rolling in.
      </p>
      <button className="btn btn--primary btn--block" onClick={goToDashboard}>
        Go to my dashboard <Arrow />
      </button>
    </>
  );
}

export default Welcome;
